import { configureStore } from "@reduxjs/toolkit";
import {
  TypedUseSelectorHook,
  useDispatch as useAppDispatch,
  useSelector as useAppSelector,
} from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { STORE_KEY } from "@/configs";
import { setShowQuote } from "@/lib/wallet/show";

import reducers from "./reducers";

type RootState = ReturnType<typeof reducers>;

// The session token lives in memory only (an XSS could read it from storage); a reload gets it
// back from the httpOnly cookie through lib/session.ts.
const persistConfig = {
  key: STORE_KEY,
  storage,
  // sportPrefs holds display and stake preferences only (no money, no token).
  whitelist: ["game", "sound", "sportPrefs"],
};

/**
 * Builds before this one persisted `auth`, token included. Taken out of storage before
 * redux-persist rehydrates (it merges every stored key, whitelisted or not) and handed once
 * to the session restore, so a player signed in before the upgrade stays signed in.
 */
let legacySessionToken: string | null = null;
try {
  const persistKey = `persist:${STORE_KEY}`;
  const raw = localStorage.getItem(persistKey);
  const persisted = raw ? (JSON.parse(raw) as Record<string, string>) : null;
  if (persisted?.auth) {
    legacySessionToken = (JSON.parse(persisted.auth) as { token?: string | null }).token || null;
    delete persisted.auth;
    localStorage.setItem(persistKey, JSON.stringify(persisted));
  }
} catch {
  // Unreadable storage: nothing to carry over.
}

export const takeLegacySessionToken = (): string | null => {
  const token = legacySessionToken;
  legacySessionToken = null;
  return token;
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Plain helpers (formatBalance) read the show quote from lib/wallet/show, not from a hook.
store.subscribe(() => {
  const { auth } = store.getState();
  setShowQuote(auth.isLoggedIn ? auth.balance?.show : null);
});

type AppDispatch = typeof store.dispatch;

export const persister = persistStore(store);
export const dispatch = store.dispatch;
export const useDispatch = () => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = useAppSelector;

export * from "./reducers/app";
export * from "./reducers/auth";
export * from "./reducers/betSlip";
export * from "./reducers/game";
export * from "./reducers/sound";
